// =============================================================================
// LiftLithe (Lithe.Fit) -- WorkoutApp.jsx V46 "PB lookup all sessions + workout header title ellipsis fix"
// Screens extracted to src/screens/workout/. Helpers in src/workoutHelpers.js.
// This file contains: auth, state, data loading, handlers, and screen routing.
// =============================================================================
import { useState, useEffect, useRef, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { supabase } from "./supabaseClient";
import { Barbell, Plus, Trash, Check, X, CaretRight, ChartBar, ArrowLeft, Lightning, Clock, MagnifyingGlass, BookOpen, Play, Target, EnvelopeSimple, Lock, Eye, EyeSlash, User, Cloud, WarningCircle, SignOut, CalendarBlank, List, FloppyDisk } from "@phosphor-icons/react";
import { THEMES, T, getHeaderBg } from "./themes";
import { TODAY_ISO, fmtDate, fmtDuration, BODY_FIELDS } from "./constants";
import { enqueue, processQueue } from "./offlineQueue";
import { useHealthConnect } from "./hooks/useHealthConnect";
import { AI_SAFETY_LAW, INJURY_LAW, sendChatMessage, getLLMUrl, llmFetch, LLM_MODEL_LIGHT } from "./llm";
import { DEFAULT_EXERCISES } from "./exerciseLibrary";
import ErrorBoundary from "./components/ErrorBoundary";
import {
  lsGet, lsSet, buildPRMap,
  ExercisePicker, ExerciseBlock, RestTimer, PlateCalculator,
  epley1RM, totalVolume, completedSets, fmtWeight, toKg, uid,
  CATEGORIES, MUSCLE_GROUPS
} from "./workoutHelpers";
import Btn from "./components/Btn";
import Field from "./components/Field";
import ComicCard from "./components/ComicCard";
import Spinner from "./components/Spinner";
import SectionLabel from "./components/SectionLabel";
import AppHeader from "./components/AppHeader";

// Workout component imports (extracted from this file)
import WorkoutScreen from "./components/workout/WorkoutScreen";
import AuthScreen from "./components/workout/AuthScreen";
import BottomNav from "./components/workout/BottomNav";

// Screen imports
import DashboardScreen from "./screens/workout/DashboardScreen";
import HistoryScreen from "./screens/workout/HistoryScreen";
import RoutinesScreen from "./screens/workout/RoutinesScreen";
import ProgrammesScreen from "./screens/workout/ProgrammesScreen";
import LibraryScreen from "./screens/workout/LibraryScreen";
import BodyMeasurementsScreen from "./screens/workout/BodyMeasurementsScreen";
import CalendarScreen from "./screens/workout/CalendarScreen";

// =============================================================================
// CALORIE BURN ESTIMATE
// =============================================================================
function estimateCaloriesBurnedLocal(exercises, durationSecs, weightKg) {
  if (!durationSecs || durationSecs < 60) return 0;
  if (!weightKg || weightKg < 20) weightKg = 75;
  const cats = new Set((exercises||[]).map(ex => ex.category || "Other"));
  const hasCardio = cats.has("Cardio");
  const hasStrength = ["Barbell","Dumbbell","Cable","Machine","Kettlebell","Band","Olympic"].some(c=>cats.has(c));
  const hasMobility = cats.has("Mobility") && cats.size === 1;
  let met;
  if (hasMobility) met = 3.0;
  else if (hasCardio && !hasStrength) met = 7.0;
  else if (hasCardio && hasStrength) met = 6.5;
  else met = 5.0;
  return Math.round(met * weightKg * (durationSecs / 3600));
}

// =============================================================================
// MAIN APP
// =============================================================================
export default function App(){
  const [session,setSession]=useState(null);
  const [authReady,setAuthReady]=useState(false);
  const [profileLoading,setProfileLoading]=useState(true);
  const loadedRef=useRef(null);
  const [profile,setProfile]=useState({username:"",weight:"",height:"",persona:"Balanced",targets:{},physique_goal:null,simple_goal:null,goal:null});
  const [sessions,setSessions]=useState([]);
  const [routines,setRoutines]=useState([]);
  const [programmes,setProgrammes]=useState([]);
  const [customExercises,setCustomExercises]=useState([]);
  const [screen,setScreen]=useState("dashboard");
  const [activeWorkout,setActiveWorkout]=useState(null);
  const [unit]=useState(()=>lsGet("wp_unit","kg"));
  const [darkMode,setDarkMode]=useState(()=>lsGet("wp_dark",false));

  // Premium gating — mirrors App.js pattern exactly. When PREMIUM_ENABLED=false, everyone has full access.
  // Uses !!profile.is_premium to match App.js's setIsPremium(!!profile.is_premium) derivation.
  const PREMIUM_ENABLED = false;
  const isPremium = !!profile.is_premium;
  const effectiveIsPremium = !PREMIUM_ENABLED || isPremium;

  const effectiveTheme = darkMode ? (profile.persona+"Dark") : profile.persona;
  Object.assign(T,THEMES[effectiveTheme]||THEMES[profile.persona]||THEMES.Balanced);

  // Post-workout AI advisor — LiftLithe variant (no nutrition totals available here)
  async function runPostWorkoutAdvisor(sess) {
    if (!effectiveIsPremium) return null;
    try {
      const exercises = sess.exercises || [];
      const muscleGroups = exercises
        .flatMap(ex => (ex.muscles || []).slice(0, 1))
        .filter((v, i, a) => a.indexOf(v) === i).filter(Boolean)
        .join(", ") || "General";
      const weightKg = parseFloat(profile?.weight) || 75;
      const caloriesBurned = estimateCaloriesBurnedLocal(exercises, sess.duration, weightKg);
      const timeOfDay = new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening";
      const totalVol = exercises.reduce((a, ex) => a + ex.sets.reduce((b, st) => b + (parseFloat(st.weight) || 0) * (parseInt(st.reps) || 0), 0), 0);
      const totalSets = exercises.reduce((a, e) => a + e.sets.filter(st => st.done).length, 0);
      const prs = exercises.filter(e => e.isPR);
      const exerciseLines = exercises.map(ex => {
        const setCount = ex.sets.filter(s => s.done).length;
        const topSet = ex.sets.reduce((best, s) => {
          const w = parseFloat(s.weight) || 0;
          return w > (parseFloat(best.weight) || 0) ? s : best;
        }, ex.sets[0] || {});
        return `  - ${ex.name} (${(ex.muscles || []).join(", ") || "general"}): ${setCount} sets, top set ${topSet.weight || 0}kg × ${topSet.reps || 0}${ex.isPR ? " ★ PR" : ""}`;
      }).join("\n");
      const prLine = prs.length > 0 ? `\nPRs SET: ${prs.map(e => e.name).join(", ")}` : "\nPRs SET: None";
      const raw = await llmFetch({
        model: LLM_MODEL_LIGHT, max_tokens: 400, temperature: 0.4,
        messages: [
          { role: "system", content: `You are a sports nutritionist giving post-workout recovery advice. SESSION_LAW: You MUST reference the specific exercises, muscles, volume, and PRs from the session data below. Generic advice is banned — every sentence must tie back to what this person actually did. ` +
            `Cover three things: (1) WHAT HAPPENED — what the workout did to the muscles in plain terms, referencing specific exercises by name, total volume, and estimated calories. ${prs.length > 0 ? "Acknowledge the PR(s) by name — celebrate the achievement." : ""} (2) WHAT TO EAT NOW — name specific UK supermarket foods to eat in the next 30-60 minutes, with amounts tailored to the session intensity and volume. (3) TOMORROW — whether to train or rest, and which muscle groups to target or avoid based on what was worked today. ` +
            `PLAIN_ENGLISH_LAW: no science jargon — banned: leucine, MPS, glycogen, substrate, anabolic, catabolic, thermogenesis. UK_LAW: UK-available food only. ${AI_SAFETY_LAW} ${INJURY_LAW}` },
          { role: "user", content: `SESSION: ${sess.name || "Workout"}\nDURATION: ${Math.round((sess.duration || 0) / 60)} mins\nCALORIES BURNED: ~${caloriesBurned} kcal\nTOTAL VOLUME: ${Math.round(totalVol).toLocaleString()}kg\nTOTAL SETS: ${totalSets}\nMUSCLES WORKED: ${muscleGroups}\n\nEXERCISES:\n${exerciseLines}${prLine}\n\nTIME_OF_DAY: ${timeOfDay}\nUSER_WEIGHT: ${weightKg}kg\n\nGive a single short paragraph (4-5 sentences). Name actual foods and amounts. Reference the specific exercises and muscles from this session.` },
        ],
      });
      return (raw || "").trim() || null;
    } catch(_) { return null; }
  }

  // headerBg removed — active workout uses T.ink; sub-screens use AppHeader

  useEffect(()=>{
    if(!document.getElementById("lithe-fonts")){
      const link=document.createElement("link");
      link.id="lithe-fonts"; link.rel="stylesheet";
      link.href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Poppins:wght@600;700;800;900&display=swap";
      document.head.appendChild(link);
    }
    document.body.style.background=T.bg;
    document.body.style.fontFamily=T.fontSans;
  },[profile.persona,darkMode]);

  const library=[...DEFAULT_EXERCISES,...customExercises.filter(c=>!DEFAULT_EXERCISES.find(d=>d.id===c.id))];
  const hc = useHealthConnect();

  useEffect(()=>{
    const handler=()=>processQueue(supabase);
    window.addEventListener("online",handler);
    return()=>window.removeEventListener("online",handler);
  },[]);

  // ─── ANDROID BACK BUTTON ────────────────────────────────────────────────────
  useEffect(()=>{
    const isNative = typeof window !== "undefined" &&
      (window.location.protocol === "capacitor:" || !!(window.Capacitor?.isNativePlatform?.()));
    if (!isNative) return;
    let cleanup = () => {};
    (async () => {
      try {
        const { App: CapApp } = await import(/* webpackIgnore: true */ "@capacitor/app");
        const handle = await CapApp.addListener("backButton", () => {
          if (activeWorkout) return; // never accidentally cancel an active workout
          if (screen !== "dashboard") {
            setScreen("dashboard");
          } else {
            // At LiftLithe root — navigate to NutriLithe
            window.location.assign("/");
          }
        });
        cleanup = () => handle.remove();
      } catch (_) { /* not available in web build */ }
    })();
    return () => cleanup();
  }, [screen, activeWorkout]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{
      setSession(session);
      if(session){ loadedRef.current=session.user.id; loadData(session.user.id); }
      else { setProfileLoading(false); }
      setAuthReady(true);
    });
    const {data:{subscription}}=supabase.auth.onAuthStateChange((event,session)=>{
      setSession(session);
      if(event==="SIGNED_IN"&&session){
        if(loadedRef.current!==session.user.id){ loadedRef.current=session.user.id; loadData(session.user.id); }
      }
      if(event==="SIGNED_OUT"){ resetState(); }
    });
    return()=>subscription.unsubscribe();
  },[]); // eslint-disable-line react-hooks/exhaustive-deps

  function resetState(){
    setSessions([]); setRoutines([]); setProgrammes([]); setCustomExercises([]);
    setProfile({username:"",weight:"",height:"",persona:"Balanced",targets:{}});
    setScreen("dashboard"); loadedRef.current=null;
    setBodyMeasurements([]);
    setActiveWorkout(null);
  }

  async function loadData(uid){
    setProfileLoading(true);
    try{
      const {data:p}=await supabase.from("profiles").select("username,weight,height,persona,targets,physique_goal,simple_goal,goal").eq("id",uid).single();
      if(p) setProfile({username:p.username||"",weight:p.weight||"",height:p.height||"",persona:p.persona||"Balanced",targets:p.targets||{},physique_goal:p.physique_goal||null,simple_goal:p.simple_goal||null,goal:p.goal||null});
      const {data:ws}=await supabase.from("workout_sessions").select("id,user_id,name,date,duration,exercises,notes,created_at,calories_burned").eq("user_id",uid).order("date",{ascending:false}).limit(100);
      setSessions(ws||[]);
      const {data:rt}=await supabase.from("workout_routines").select("id,user_id,name,exercises,created_at").eq("user_id",uid).order("created_at",{ascending:false});
      setRoutines(rt||[]);
      const {data:pg}=await supabase.from("workout_programmes").select("id,user_id,name,weeks,active,started_at,created_at").eq("user_id",uid).order("created_at",{ascending:false});
      setProgrammes(pg||[]);
      await loadBodyMeasurements(uid);
    } catch(e){ Sentry.captureException(e, { extra: { context: "loadData" } }); if (process.env.NODE_ENV !== "production") { console.error("[loadData]",e); } }
    setProfileLoading(false);
  }

  async function handleLogout(){ await supabase.auth.signOut(); }

  function handleStartWorkout(template){ setActiveWorkout(template); setScreen("workout"); }
  function handleStartRoutine(routine){
    const template={name:routine.name,exercises:(routine.exercises||[]).map(ex=>{
      // Programme-generated routines store sets as a number + reps as a string;
      // builder-created routines store sets as an array of objects
      const setsArr = Array.isArray(ex.sets)
        ? ex.sets.map(s=>({...s,done:false}))
        : Array.from({length:parseInt(ex.sets)||3},()=>({reps:ex.reps||"",weight:ex.defaultWeight||"",rpe:"",done:false}));
      return {exerciseId:ex.exerciseId,name:ex.name,sets:setsArr};
    })};
    setActiveWorkout(template); setScreen("workout");
  }

  async function handleFinishWorkout(sess){
    if(!session) return;
    const weightKg = parseFloat(profile?.weight) || 75;
    const cappedDuration = Math.min(sess.duration || 0, 18000); // cap 5 hours in seconds
    const caloriesBurned = estimateCaloriesBurnedLocal(sess.exercises, cappedDuration, weightKg);
    const row={user_id:session.user.id,name:sess.name,date:sess.date,duration:cappedDuration,exercises:sess.exercises,notes:"",calories_burned:caloriesBurned};
    if(!navigator.onLine){
      enqueue("workout_sessions","insert",row,session.user.id);
      setSessions(prev=>[{...row,id:"pending_"+Date.now()},...prev]);
    } else {
      const {data,error}=await supabase.from("workout_sessions").insert(row).select().single();
      if(!error&&data) setSessions(prev=>[data,...prev]);
    }
    setActiveWorkout(null); setScreen("history");
    // Check workout achievements — fire and forget
    checkWorkoutAchievements(sess);
  }

  function checkWorkoutAchievements(sess) {
    const uid = session?.user?.id;
    if (!uid) return;
    (async () => {
      try {
        const [{ data: existing }, { count }] = await Promise.all([
          supabase.from("achievements").select("achievement_id").eq("user_id", uid),
          supabase.from("workout_sessions").select("id", { count: "exact", head: true }).eq("user_id", uid),
        ]);
        const earned = new Set((existing||[]).map(a => a.achievement_id));
        const workoutCount = count || 0;
        const newlyUnlocked = [];

        if (workoutCount >= 1   && !earned.has("first_workout"))  newlyUnlocked.push("first_workout");
        if (workoutCount >= 10  && !earned.has("workouts_10"))    newlyUnlocked.push("workouts_10");
        if (workoutCount >= 50  && !earned.has("workouts_50"))    newlyUnlocked.push("workouts_50");
        if (workoutCount >= 100 && !earned.has("workouts_100"))   newlyUnlocked.push("workouts_100");

        // first_pr — compare this session's sets against the prMap built from prior sessions
        if (!earned.has("first_pr")) {
          const hasNewPR = (sess.exercises||[]).some(ex => {
            const prev = prMap[ex.exerciseId];
            return (ex.sets||[]).some(s => {
              if (!s.done || !s.weight) return false;
              const w = parseFloat(s.weight);
              return !prev || w > parseFloat(prev.maxWeight);
            });
          });
          if (hasNewPR) newlyUnlocked.push("first_pr");
        }

        for (const achId of newlyUnlocked) {
          await supabase.from("achievements").insert({ user_id: uid, achievement_id: achId });
        }
      } catch(_) {}
    })();
  }

  async function handleDeleteSession(id){
    const deletedSession = sessions.find(s=>s.id===id);
    setSessions(prev=>prev.filter(s=>s.id!==id));
    try { await supabase.from("workout_sessions").delete().eq("id",id); }
    catch(e) { Sentry.captureException(e, { extra: { context: "handleDeleteSession" } }); if (process.env.NODE_ENV !== "production") { console.error("[handleDeleteSession]",e); } if (deletedSession) setSessions(prev=>[...prev, deletedSession]); window.alert("Failed to delete session. It has been restored."); }
  }

  async function handleUpdateSession(id, updates){
    const prev = sessions.find(s=>s.id===id);
    setSessions(ss=>ss.map(s=>s.id===id?{...s,...updates}:s));
    try { await supabase.from("workout_sessions").update(updates).eq("id",id); }
    catch(e) { Sentry.captureException(e, { extra: { context: "handleUpdateSession" } }); if(process.env.NODE_ENV!=="production") console.error("[handleUpdateSession]",e); if(prev) setSessions(ss=>ss.map(s=>s.id===id?prev:s)); }
  }

  async function handleSaveRoutine({id,name,exercises}){
    if(!session) return null;
    if(id){
      if(!navigator.onLine){ enqueue("workout_routines","update",{id,name,exercises},session.user.id); setRoutines(prev=>prev.map(r=>r.id===id?{...r,name,exercises}:r)); return {id,name,exercises}; }
      else { try { const {data,error}=await supabase.from("workout_routines").update({name,exercises}).eq("id",id).select().single(); if(!error&&data){ setRoutines(prev=>prev.map(r=>r.id===id?data:r)); return data; } } catch(e){ Sentry.captureException(e, { extra: { context: "handleSaveRoutine" } }); if(process.env.NODE_ENV!=="production") console.error("[handleSaveRoutine]",e); } }
    } else {
      const payload={user_id:session.user.id,name,exercises};
      if(!navigator.onLine){ const pending={...payload,id:"pending_"+Date.now(),created_at:new Date().toISOString()}; enqueue("workout_routines","insert",payload,session.user.id); setRoutines(prev=>[pending,...prev]); return pending; }
      else { const {data,error}=await supabase.from("workout_routines").insert(payload).select().single(); if(!error&&data){ setRoutines(prev=>[data,...prev]); return data; } }
    }
    return null;
  }
  async function handleDeleteRoutine(id){
    if(!window.confirm("Delete this routine? This cannot be undone.")) return;
    setRoutines(prev=>prev.filter(r=>r.id!==id));
    if(!navigator.onLine){ enqueue("workout_routines","delete",{id},session?.user?.id); }
    else { try { await supabase.from("workout_routines").delete().eq("id",id); } catch(e){ Sentry.captureException(e, { extra: { context: "handleDeleteRoutine" } }); if(process.env.NODE_ENV!=="production") console.error("[handleDeleteRoutine]",e); } }
  }

  async function handleSaveProgramme({id,name,weeks,active,started_at}){
    if(!session) return;
    if(id){
      const payload={id,name,weeks,active,started_at};
      if(!navigator.onLine){ enqueue("workout_programmes","update",payload,session.user.id); setProgrammes(prev=>prev.map(p=>p.id===id?{...p,name,weeks,active,started_at}:p)); }
      else { const {data,error}=await supabase.from("workout_programmes").update({name,weeks,active,started_at}).eq("id",id).select().single(); if(!error&&data) setProgrammes(prev=>prev.map(p=>p.id===id?data:p)); }
    } else {
      const payload={user_id:session.user.id,name,weeks,active:false,started_at:null};
      if(!navigator.onLine){ enqueue("workout_programmes","insert",payload,session.user.id); setProgrammes(prev=>[{...payload,id:"pending_"+Date.now(),created_at:new Date().toISOString()},...prev]); }
      else { const {data,error}=await supabase.from("workout_programmes").insert(payload).select().single(); if(!error&&data) setProgrammes(prev=>[data,...prev]); }
    }
  }
  async function handleDeleteProgramme(id){
    if(!window.confirm("Delete this programme? This cannot be undone.")) return;
    setProgrammes(prev=>prev.filter(p=>p.id!==id));
    if(!navigator.onLine){ enqueue("workout_programmes","delete",{id},session?.user?.id); }
    else { try { await supabase.from("workout_programmes").delete().eq("id",id); } catch(e){ Sentry.captureException(e, { extra: { context: "handleDeleteProgramme" } }); if(process.env.NODE_ENV!=="production") console.error("[handleDeleteProgramme]",e); } }
  }
  async function handleSetActive(id){
    if(!session) return;
    try {
      for(const p of programmes){
        if(p.active||p.id===id){
          const nowActive=p.id===id&&id!==null;
          const started=nowActive&&!p.started_at?TODAY_ISO():p.started_at;
          await supabase.from("workout_programmes").update({active:nowActive,started_at:started}).eq("id",p.id);
        }
      }
    } catch(e){ Sentry.captureException(e, { extra: { context: "handleSetActive" } }); if(process.env.NODE_ENV!=="production") console.error("[handleSetActive]",e); }
    setProgrammes(prev=>prev.map(p=>({...p,active:p.id===id&&id!==null,started_at:p.id===id&&id!==null&&!p.started_at?TODAY_ISO():p.started_at})));
  }

  const [bodyMeasurements,setBodyMeasurements]=useState([]);
  const [bodyMeasForm,setBodyMeasForm]=useState({});
  const [showBodyMeas,setShowBodyMeas]=useState(false);
  const [bodyMeasLoading,setBodyMeasLoading]=useState(false);

  async function loadBodyMeasurements(uid){
    const {data}=await supabase.from("body_measurements").select("id,user_id,date,weight_kg,body_fat_pct,chest_cm,waist_cm,hips_cm,arms_cm,thighs_cm,neck_cm,shoulders_cm,notes,logged_at").eq("user_id",uid).order("logged_at",{ascending:false}).limit(30);
    const {data:wl}=await supabase.from("weight_logs").select("id,user_id,weight_kg,note,logged_at").eq("user_id",uid).order("logged_at",{ascending:false}).limit(30);
    const existing=data||[];
    const merged=[...existing];
    (wl||[]).forEach(w=>{
      const dateStr=w.logged_at?w.logged_at.slice(0,10):TODAY_ISO();
      const alreadyHas=merged.some(m=>m.date===dateStr&&m.weight_kg!=null);
      if(!alreadyHas) merged.push({id:"wl_"+w.id,date:dateStr,logged_at:w.logged_at,weight_kg:parseFloat(w.weight_kg),_fromNutri:true});
    });
    merged.sort((a,b)=>b.logged_at<a.logged_at?-1:1);
    setBodyMeasurements(merged.slice(0,60));
  }
  async function saveBodyMeasurement(form){
    if(!session) return;
    setBodyMeasLoading(true);
    try{
      const row={user_id:session.user.id,date:TODAY_ISO(),logged_at:new Date().toISOString()};
      BODY_FIELDS.forEach(f=>{ if(form[f.key]!==undefined&&form[f.key]!=="") row[f.key]=parseFloat(form[f.key]); });
      if(!navigator.onLine){
        enqueue("body_measurements","insert",row,session.user.id);
        setBodyMeasurements(prev=>[{...row,id:"pending_"+Date.now()},...prev].slice(0,30));
        setBodyMeasForm({}); setShowBodyMeas(false);
      } else {
        const {data,error}=await supabase.from("body_measurements").insert(row).select().single();
        if(error) throw error;
        setBodyMeasurements(prev=>[data,...prev].slice(0,30));
        setBodyMeasForm({}); setShowBodyMeas(false);
      }
    }catch(e){ Sentry.captureException(e, { extra: { context: "saveBodyMeasurement" } }); if(process.env.NODE_ENV!=="production") console.error("[saveBodyMeasurement]",e); }
    setBodyMeasLoading(false);
  }

  function handleAddExercise(ex){
    const next=[...customExercises,ex];
    setCustomExercises(next);
    lsSet("wp_custom_ex_"+session?.user?.id,next);
    if(session?.user?.id){
      supabase.from("custom_exercises")
        .insert({id:ex.id,user_id:session.user.id,name:ex.name,category:ex.category,muscles:ex.muscles,notes:ex.notes||""})
        .then(({error})=>{ if(!error) lsSet("wp_custom_ex_"+session.user.id,next); })
        .catch(()=>{});
    }
  }
  function handleDeleteExercise(id){
    const next=customExercises.filter(e=>e.id!==id);
    setCustomExercises(next);
    lsSet("wp_custom_ex_"+session?.user?.id,next);
    if(session?.user?.id){
      supabase.from("custom_exercises")
        .delete().eq("id",id).eq("user_id",session.user.id)
        .then(()=>{}).catch(()=>{});
    }
  }
  useEffect(()=>{
    if(!session?.user?.id) return;
    const uid=session.user.id;
    const lsKey="wp_custom_ex_"+uid;
    (async()=>{
      try{
        const {data,error}=await supabase.from("custom_exercises").select("*").eq("user_id",uid).order("created_at");
        if(!error&&data){
          if(data.length===0){
            // One-time migration: push any existing localStorage items to Supabase
            const local=lsGet(lsKey,[]);
            if(local.length>0){
              const rows=local.map(ex=>({id:ex.id,user_id:uid,name:ex.name,category:ex.category,muscles:ex.muscles,notes:ex.notes||""}));
              const {error:upsertErr}=await supabase.from("custom_exercises").upsert(rows,{onConflict:"id"});
              if(!upsertErr){
                try{ localStorage.removeItem(lsKey); }catch(_){}
                setCustomExercises(local);
                return;
              }
            }
          }
          setCustomExercises(data);
          lsSet(lsKey,data);
        }else{
          // Supabase unavailable — fall back to localStorage silently
          setCustomExercises(lsGet(lsKey,[]));
        }
      }catch(_){
        setCustomExercises(lsGet(lsKey,[]));
      }
    })();
  },[session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const prMap=useMemo(()=>buildPRMap(sessions),[sessions]);

  // ── Loading gate ────────────────────────────────────────────────────────
  if(!authReady||(session&&profileLoading)){
    return(
      <div style={{background:T.bg,minHeight:"100vh",width:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div style={{textAlign:"center"}}>
          <Spinner size={32}/>
          <p style={{color:T.textSub,fontSize:13,fontFamily:T.fontSans,fontWeight:"bold",marginTop:16}}>
            {!authReady?"Connecting...":"Loading your data..."}
          </p>
        </div>
      </div>
    );
  }

  if(!session) return <ErrorBoundary screenName="LiftLithe Auth"><AuthScreen persona={profile.persona}/></ErrorBoundary>;

  // ── Workout chat context builder (passed to DashboardScreen) ──────────────
  function buildWorkoutChatContext() {
    const last3 = [...sessions].sort((a,b) => b.date < a.date ? -1 : 1).slice(0, 3);
    const sessLines = last3.map(s => {
      const muscles = (s.exercises||[]).flatMap(ex => {
        const lx = library.find(e => e.id === ex.exerciseId);
        return lx?.muscles || [];
      });
      const uniqueMuscles = [...new Set(muscles)];
      const rpeSets = (s.exercises||[]).flatMap(ex => (ex.sets||[]).filter(st => st.done && st.rpe));
      const avgRpe = rpeSets.length > 0 ? (rpeSets.reduce((sum, st) => sum + parseFloat(st.rpe), 0) / rpeSets.length).toFixed(1) : "N/A";
      return `${s.date} | ${s.name||"Workout"} | ${Math.round((s.duration||0)/60)}min | Muscles: ${uniqueMuscles.join(", ")||"unknown"} | RPE: ${avgRpe}`;
    }).join("\n");
    const now48h = new Date(); now48h.setHours(now48h.getHours() - 48);
    const recent48h = sessions.filter(s => new Date(s.date) >= now48h);
    const musclesLast48h = [...new Set(recent48h.flatMap(s => (s.exercises||[]).flatMap(ex => {
      const lx = library.find(e => e.id === ex.exerciseId);
      return lx?.muscles || [];
    })))];
    const activeRoutine = routines.find(r => r.id === (programmes.find(p => p.active)?.weeks?.[0]?.days?.[0]?.routineId));
    const dayName = new Date().toLocaleDateString("en-GB", { weekday: "long" });
    return `You are a workout assistant for the Lithe.Fit app.
Only answer questions about exercise, recovery, and training. Decline anything else politely.

USER CONTEXT:
Day: ${dayName}
Current routine: ${activeRoutine?.name || "None set"}
Muscles trained in last 48h: ${musclesLast48h.join(", ") || "none"}
Last 3 sessions:
${sessLines || "No sessions yet"}

${AI_SAFETY_LAW}
${INJURY_LAW}
PLAIN_ENGLISH_LAW: Plain English only. No science jargon.
UK_LAW: UK English throughout.
SCOPE_LAW: Only answer questions about workouts and recovery. If asked anything outside this scope say: "I can only help with workouts and recovery — try asking me about that instead."
BANNED TERMS: leucine, MPS, glycogen, substrate, anabolic, catabolic, thermogenesis, catabolism, ketogenic state, metabolic efficiency, satiety signals, lean mass preservation, metabolic rate, normal metabolic state, muscle protein synthesis, glycogen resynthesis, leucine threshold, substrate utilisation, basal metabolic rate, kickstart your metabolism.
CATABOLISM SYNONYM BAN: Never say "burned muscle for energy", "breaking down muscle tissue", "body is eating muscle", "muscle wasting".`;
  }

  return(
    <ErrorBoundary screenName="LiftLithe">
    <div style={{background:T.bg,minHeight:"100dvh",width:"100%",fontFamily:T.fontSans,paddingBottom:screen==="workout"?0:110,overflow:"hidden"}}>
      {screen==="workout"?(
        <div style={{background:T.bg,minHeight:"100dvh",width:"100%"}}>
          <ErrorBoundary screenName="Workout">
            <WorkoutScreen library={library} unit={unit} templateSession={activeWorkout} sessions={sessions} profile={profile} onFinish={handleFinishWorkout} onCancel={()=>{setActiveWorkout(null);setScreen("dashboard");}} onRunAdvisor={runPostWorkoutAdvisor} effectiveIsPremium={effectiveIsPremium}/>
          </ErrorBoundary>
        </div>
      ):(
        <>
          {screen==="dashboard"&&<ErrorBoundary screenName="Dashboard"><DashboardScreen sessions={sessions} library={library} unit={unit} profile={profile} prMap={prMap} routines={routines} programmes={programmes} onStartWorkout={handleStartWorkout} onStartRoutine={handleStartRoutine} onViewHistory={()=>setScreen("history")} darkMode={darkMode} setDarkMode={setDarkMode} hcData={hc} persona={profile.persona} buildWorkoutChatContext={buildWorkoutChatContext} sendChatMessage={sendChatMessage} getLLMUrl={getLLMUrl} userId={session?.user?.id} effectiveIsPremium={effectiveIsPremium}/></ErrorBoundary>}
          {screen==="history"&&<ErrorBoundary screenName="History"><HistoryScreen sessions={sessions} library={library} unit={unit} prMap={prMap} onDeleteSession={handleDeleteSession} onUpdateSession={handleUpdateSession} persona={profile.persona} darkMode={darkMode} setDarkMode={setDarkMode} profile={profile}/></ErrorBoundary>}
          {screen==="routines"&&<ErrorBoundary screenName="Routines"><RoutinesScreen routines={routines} library={library} onSaveRoutine={handleSaveRoutine} onDeleteRoutine={handleDeleteRoutine} onStartRoutine={handleStartRoutine} persona={profile.persona} darkMode={darkMode} setDarkMode={setDarkMode} profile={profile}/></ErrorBoundary>}
          {screen==="programmes"&&<ErrorBoundary screenName="Programmes"><ProgrammesScreen programmes={programmes} routines={routines} sessions={sessions} library={library} onSaveProgramme={handleSaveProgramme} onSaveRoutine={handleSaveRoutine} onDeleteProgramme={handleDeleteProgramme} onSetActive={handleSetActive} persona={profile.persona} profile={profile} effectiveIsPremium={effectiveIsPremium} userId={session?.user?.id} darkMode={darkMode} setDarkMode={setDarkMode} onNavigate={setScreen}/></ErrorBoundary>}
          {screen==="library"&&<ErrorBoundary screenName="Library"><LibraryScreen library={library} onAddExercise={handleAddExercise} onDeleteExercise={handleDeleteExercise} persona={profile.persona} darkMode={darkMode} setDarkMode={setDarkMode} profile={profile}/></ErrorBoundary>}
          {screen==="body"&&<ErrorBoundary screenName="Body Measurements"><BodyMeasurementsScreen bodyMeasurements={bodyMeasurements} bodyMeasForm={bodyMeasForm} setBodyMeasForm={setBodyMeasForm} showBodyMeas={showBodyMeas} setShowBodyMeas={setShowBodyMeas} bodyMeasLoading={bodyMeasLoading} saveBodyMeasurement={saveBodyMeasurement} persona={profile.persona} sessions={sessions} library={library} darkMode={darkMode} setDarkMode={setDarkMode} profile={profile}/></ErrorBoundary>}
          {screen==="calendar"&&<ErrorBoundary screenName="Calendar"><CalendarScreen sessions={sessions} onSelectSession={s=>{setScreen("history");}} persona={profile.persona} darkMode={darkMode} setDarkMode={setDarkMode} profile={profile} onNavigate={setScreen}/></ErrorBoundary>}
          <BottomNav screen={screen} onNav={setScreen} onLogout={handleLogout}/>
        </>
      )}
    </div>
    </ErrorBoundary>
  );
}
